<template>
    <BaseMapShell v-model:panelOpen="panelOpen" v-model:rightPanelOpen="rightPanelOpen" :center="center" :zoom="zoom"
        :bounds="bounds" :max-bounds="maxBounds" :maxZoom="maxZoom" :minZoom="minZoom" :enableAddressSearch="true"
        :has-active-panel-filters="hasActiveGroupFilter"
        :panel-filters-label="hasActiveGroupFilter ? `Groupes ${selectedGroupsCount}/${groupOptions.length}` : ''"
        @leaflet-zoom="onUserZoom" @leaflet-center="onUserCenter">
        <template #map-overlays="{ mapInstance, mapGetter }">
            <GpsTrackingControl v-if="showGpsControl" :map="mapInstance" :map-getter="mapGetter"
                @position-change="onGpsPositionChange" />
        </template>

        <!-- Toolbar : champ de recherche aligné avec le bouton -->
        <template #toolbar-right>
            <SearchDisplay :searchFunction="'searchWord'"
                @searchWord="(word) => { this.searchInput = word; this.refreshMap() }" />
        </template>

        <!-- Panneau : sections Groupes + Geofencings -->
        <template #panel-sections>
            <PanelSectionGroups v-if="canFilterByGroups" :groups="groupOptions" @toggle-group="onToggleGroup"
                @hide-all="hideAllGroups" />

            <PanelSectionGeofencing :geofencings="geofencings" :selected-id="selectedGeofencingId"
                :loading="loadingGeofencingList || loadingGeofencingGeom" @refresh="fetchGeofencings"
                @select="handleSelectGeofencing" />
        </template>

        <!-- Couches spécifiques de la carte principale -->
        <l-circle v-if="gpsPosition" :lat-lng="gpsPosition" :radius="gpsAccuracy || 15" :color="'#0d6efd'" :fill="true"
            :fillOpacity="0.12" :weight="2" />
        <l-marker v-if="gpsPosition" :lat-lng="gpsPosition" />

        <l-layer-group v-for="bGroup in filteredGroups" :key="bGroup.groupId" layer-type="overlay"
            :name="bGroup.groupName" :visible="true">
            <BaliseMarker v-for="balise in bGroup.balises" :key="balise.i + '-' + balise.parkingLat" :balise="balise"
                @click="openRightPanel(balise)" />
        </l-layer-group>

        <!-- Geofencing sélectionné : polygone -->
        <l-layer-group v-if="selectedGeofencingPolygon && selectedGeofencingPolygon.length >= 3" layer-type="overlay"
            name="Geofencing" :visible="true">
            <l-polygon :lat-lngs="selectedGeofencingPolygon" :fill="true" :fillOpacity="0.15" :weight="3"
                :color="'#20c997'" />
        </l-layer-group>

        <!-- === Geofencing lié au véhicule sélectionné === -->
        <l-layer-group v-if="
            rightPanelOpen
            && rightPanelMode === 'detail'
            && selectedBalise
            && vehicleGeofencingPolygon
            && vehicleGeofencingPolygon.length >= 3
        " layer-type="overlay" name="Geofencing véhicule" :visible="true">
            <l-polygon :lat-lngs="vehicleGeofencingPolygon" :fill="true" :fillOpacity="0.12" :weight="3"
                :dashArray="[8, 6]" :color="'#0d6efd'" />
        </l-layer-group>

        <!-- === Corridoring lié au véhicule sélectionné (polyline + radius) === -->
        <template v-if="
            rightPanelOpen
            && rightPanelMode === 'detail'
            && selectedBalise
            && vehicleCorridoringPositions
            && vehicleCorridoringPositions.length
        ">
            <NotificationMaker v-if="vehicleCorridoringStart" notification="start"
                :latitude="vehicleCorridoringStart.latitude" :longitude="vehicleCorridoringStart.longitude" />
            <NotificationMaker v-if="vehicleCorridoringEnd" notification="finish"
                :latitude="vehicleCorridoringEnd.latitude" :longitude="vehicleCorridoringEnd.longitude" />

            <CorridoringDisplay v-if="vehicleCorridoringPolyline" :polyline="vehicleCorridoringPolyline" />

            <l-circle v-for="(pos, idx) in vehicleCorridoringPositions" :key="'veh-corr-' + idx"
                :lat-lng="[pos.latitude, pos.longitude]" :radius="pos.radius || 200"
                :color="pos.radius ? 'blue' : 'red'" :opacity="1" :fillOpacity="0.12" :weight="2" />
        </template>
        <!-- Contenu injecté dans le Right Panel du Shell -->
        <template #right-panel>
            <RightVehiclePicker v-if="rightPanelMode === 'pick'" :balises="nearbyBalises" @select="openDetail" />
            <RightVehiclePanel v-else-if="selectedBalise" :balise="selectedBalise"
                :followMode="this.followedBaliseId !== null" @follow="followMarker" @unfollow="unFollowBalise"
                @setParking="setParking" @removeParking="removeParking" @vehicleReport="onVehicleReport" />
        </template>
    </BaseMapShell>
</template>

<script>
import { LLayerGroup, LPolygon, LCircle, LMarker } from "@vue-leaflet/vue-leaflet";
import { Capacitor } from "@capacitor/core";
import BaseMapShell from "../BaseMapShell.vue";
import GpsTrackingControl from "../Components/GpsTrackingControl.vue";
import PanelSectionGroups from "../Components/sidePanel/PanelGroupsSection.vue";
import PanelSectionGeofencing from "../Components/sidePanel/PanelSectionGeofencing.vue";
import BaliseMarker from "../Components/BaliseMarker.vue";
import RightVehiclePanel from "../Components/rightPanel/RightVehiclePanel.vue";
import RightVehiclePicker from "../Components/rightPanel/RightVehiclePicker.vue";
import SearchDisplay from "@/components/display/SearchDisplay.vue";
import BaliseRepository from "@/repositories/balise.repo.js";
import GroupRepository from "@/repositories/group.repo.js";
import BaliseService from "@/services/balise.service.js";
import ReportService from "@/services/report.service.js";
import FormatHelper from "@/helpers/format.helper.js";
import UserHelper from "@/helpers/user.helper.js";
import GeofencingService from "@/services/geofencing.service.js";
import { computePaddedBounds } from "../composables/useBounds";
import CorridoringService from "@/services/corridoring.service.js";
import { getCorridoringPolyline } from "../composables/usePolyline";
import { normalizePositions } from "../composables/usePositions";
import NotificationMaker from "../Components/NotificationMarker.vue";
import CorridoringDisplay from "../Components/CorridoringDisplay.vue";
import { readUserPageFilters, writeUserPageFilters } from "@/helpers/localUserFilterStorage.js";

const PAGE_FILTER_KEY = "main-map";
const DEFAULT_CENTER = [48.9571487, 2.1941876];
const DEFAULT_ZOOM = 15;
const DEFAULT_MIN_ZOOM = 3;
const DEFAULT_MAX_ZOOM = 19;

export default {
    name: "MainMap",
    components: {
        BaseMapShell,
        GpsTrackingControl,
        PanelSectionGroups,
        PanelSectionGeofencing,
        BaliseMarker,
        RightVehiclePanel,
        RightVehiclePicker,
        SearchDisplay,
        LLayerGroup,
        LPolygon,
        LCircle,
        LMarker,
        NotificationMaker,
        CorridoringDisplay,
    },
    data() {
        return {
            panelOpen: false,
            rightPanelOpen: false,
            selectedBalise: null,
            rightPanelMode: 'detail', // 'detail' | 'pick'
            nearbyBalises: [],
            zoom: DEFAULT_ZOOM,
            center: DEFAULT_CENTER,
            bounds: [],
            gpsPosition: null,
            gpsAccuracy: null,
            isNativePlatform: Capacitor.isNativePlatform(),
            maxBounds: [],
            timer: "",
            maxZoom: DEFAULT_MAX_ZOOM,
            minZoom: DEFAULT_MIN_ZOOM,
            groups: [],
            balises: [],
            visibleGroupIds: new Set(),
            hasUserToggledGroups: false,
            searchInput: null,
            userViewLocked: false,
            // Geofencings
            geofencings: [],
            selectedGeofencingId: null,
            selectedGeofencingPolygon: [],
            loadingGeofencingList: false,
            loadingGeofencingGeom: false,
            // --- Overlays liés au véhicule sélectionné ---
            vehicleGeofencingId: null,
            vehicleGeofencingPolygon: [],
            vehicleCorridoringId: null,
            vehicleCorridoringPositions: [],
            vehicleCorridoringPolyline: null,
            vehicleCorridoringStart: null,
            vehicleCorridoringEnd: null,
        };
    },
    computed: {
        showGpsControl() {
            return (
                this.isNativePlatform &&
                Capacitor.getPlatform() !== "android" &&
                this.followedBaliseId === null
            );
        },
        followedBaliseId() {
            return this.$route?.params?.bid ?? null;
        },
        isAllClientsMode() {
            return (this.$isAdmin || this.$isOperator) && Number(UserHelper.getClientId()) === 0;
        },
        currentUserId() {
            return this.$store?.state?.auth?.user?.id ?? null;
        },
        canFilterByGroups() {
            return !this.isAllClientsMode && Number(this.effectiveClientId) > 0;
        },
        availableGroups() {
            if (!this.canFilterByGroups) return [];

            return [
                ...this.groups,
                {
                    id: 0,
                    name: "Sans groupe",
                },
            ];
        },
        groupOptions() {
            return this.availableGroups.map((group) => ({
                id: group.id,
                name: group.name,
                selected: this.visibleGroupIds.has(String(group.id)),
            }));
        },
        balisesWithPosition() {
            if (!Array.isArray(this.balises)) return [];
            return this.balises.filter((balise) => balise?.lastLatitude !== null && balise?.lastLongitude !== null);
        },
        filteredBalises() {
            if (this.followedBaliseId !== null || !this.canFilterByGroups) {
                return this.balisesWithPosition;
            }

            return this.balisesWithPosition.filter((balise) => {
                const groupId = balise.groupId ?? 0;
                return this.visibleGroupIds.has(String(groupId));
            });
        },
        filteredGroups() {
            const groupsMap = new Map(
                this.availableGroups.map((group) => [
                    String(group.id),
                    {
                        groupId: group.id,
                        groupName: group.name,
                        balises: [],
                    },
                ])
            );

            this.filteredBalises.forEach((balise) => {
                const groupId = String(balise.groupId ?? 0);

                if (!groupsMap.has(groupId)) {
                    groupsMap.set(groupId, {
                        groupId: balise.groupId ?? 0,
                        groupName: balise.groupName || "Sans groupe",
                        balises: [],
                    });
                }

                groupsMap.get(groupId).balises.push(balise);
            });

            return Array.from(groupsMap.values()).filter((group) => group.balises.length > 0);
        },
        // Client pour les geofencings (même logique que GeofencingMap)
        effectiveClientId() {
            const fromStore = this.$store?.state?.auth?.user?.clientId ?? null;
            const fromWatcher = UserHelper?.getClientId?.() ?? null; // 0 = tous clients
            return fromStore ?? (fromWatcher && Number(fromWatcher) > 0 ? Number(fromWatcher) : null);
        },
        selectedGroupsCount() {
            return Array.isArray(this.groupOptions)
                ? this.groupOptions.filter(g => !!g.selected).length
                : 0;
        },
        hasActiveGroupFilter() {
            return this.groupOptions.length > 0 && this.selectedGroupsCount < this.groupOptions.length;
        },
    },
    watch: {
        '$route.params.bid': {
            immediate: true,
            handler(newVal) {
                if (newVal != null) {
                    this.gpsPosition = null;
                    this.gpsAccuracy = null;
                }
                this.applyFollowRouteState();
            },
        },
        selectedBalise: {
            immediate: false,
            handler(newVal, oldVal) {
                if (!newVal) return;

                const oldCorr = oldVal ? oldVal.corridoringId || null : null;
                const oldGeo = oldVal ? oldVal.geofencingId || null : null;
                const newCorr = newVal.corridoringId || null;
                const newGeo = newVal.geofencingId || null;

                if (!oldVal || newVal.id !== oldVal.id || newCorr !== oldCorr || newGeo !== oldGeo) {
                    this.loadVehicleZones(newVal);
                }
            },
        },
    },
    created() {
        this.fetchGroups();
        this.timer = setInterval(this.refreshMap, 5000);
        this.fetchGeofencings();
    },
    mounted() {
        this.restoreGroupFilters();
    },
    beforeUnmount() {
        this.cancelAutoUpdate();
    },
    methods: {
        onGpsPositionChange({ position, accuracy }) {
            this.gpsPosition = Array.isArray(position) ? position : null;
            this.gpsAccuracy = Number.isFinite(accuracy) ? accuracy : null;
        },
        restoreGroupFilters() {
            const saved = readUserPageFilters(this.currentUserId, PAGE_FILTER_KEY);
            const savedIds = Array.isArray(saved.visibleGroupIds) ? saved.visibleGroupIds : null;

            if (savedIds === null) {
                this.hasUserToggledGroups = false;
                return;
            }

            this.hasUserToggledGroups = true;
            this.visibleGroupIds = new Set(savedIds.map(id => String(id)));
        },
        persistGroupFilters() {
            writeUserPageFilters(this.currentUserId, PAGE_FILTER_KEY, {
                visibleGroupIds: Array.from(this.visibleGroupIds).map(id => String(id))
            });
        },
        onUserZoom(newZoom) {
            this.zoom = newZoom;
            this.userViewLocked = true;
        },
        onUserCenter(newCenter) {
            if (Array.isArray(newCenter)) {
                this.center = newCenter;
            } else if (
                newCenter
                && Number.isFinite(newCenter.lat)
                && Number.isFinite(newCenter.lng)
            ) {
                this.center = [newCenter.lat, newCenter.lng];
            }
            this.userViewLocked = true;
        },
        openRightPanel(balise) {
            // 🛰️ En mode suivi, on ne change JAMAIS de véhicule suivi et
            // on ne lance pas le picker "plusieurs véhicules proches".
            if (this.followedBaliseId !== null) {
                // On retrouve le véhicule suivi dans les données courantes,
                // sinon on se rabat sur le balise cliquée (qui est normalement ce même véhicule).
                const all = this.getAllVisibleBalises();
                const current = all.find(b => String(b.id) === String(this.followedBaliseId)) || balise;

                this.openDetail(current);
                this.rightPanelMode = 'detail';
                this.rightPanelOpen = true;
                return;
            }

            // 🔍 Mode normal : on garde le comportement "picker"
            const candidates = this.findNearbyBalises(balise, 25); // rayon en mètres
            if (candidates.length > 1) {
                this.nearbyBalises = candidates;
                this.rightPanelMode = 'pick';
                this.selectedBalise = null;
                // On efface les zones du véhicule précédent :
                this.clearVehicleZones();
            } else {
                this.openDetail(candidates[0] || balise);
            }
            this.rightPanelOpen = true;
        },
        openDetail(balise) {
            this.selectedBalise = balise;
            this.rightPanelMode = 'detail';
        },
        getAllVisibleBalises() {
            return this.filteredBalises;
        },
        findNearbyBalises(target, radiusMeters = 25) {
            const all = this.getAllVisibleBalises();
            return all
                .filter(b => this.haversineMeters(
                    target.lastLatitude, target.lastLongitude,
                    b.lastLatitude, b.lastLongitude
                ) <= radiusMeters)
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        },
        haversineMeters(lat1, lon1, lat2, lon2) {
            const toRad = d => d * Math.PI / 180;
            const R = 6371000; // m
            const dLat = toRad(lat2 - lat1);
            const dLon = toRad(lon2 - lon1);
            const a = Math.sin(dLat / 2) ** 2 +
                Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
                Math.sin(dLon / 2) ** 2;
            return 2 * R * Math.asin(Math.sqrt(a));
        },
        applyFollowRouteState() {
            this.userViewLocked = false;
            if (this.followedBaliseId !== null) {
                this.bounds = null;
                this.zoom = 16;
                this.minZoom = 7;
                this.maxZoom = 18;
            } else {
                this.maxBounds = [];
                this.minZoom = 3;
                this.maxZoom = 19;
            }
            this.refreshMap();
        },
        unFollowBalise() {
            this.$router.push({ name: 'MapPage' }).then(() => {
                this.applyFollowRouteState();
            });
        },
        updateSelectedFromBalises() {
            if (!this.selectedBalise || !Array.isArray(this.balises)) return;
            const found = this.balises.find(b => b.id === this.selectedBalise.id);
            if (found) this.selectedBalise = found;
        },
        getAvailableGroupIds() {
            return new Set(this.availableGroups.map(group => String(group.id)));
        },
        syncVisibleGroupIdsWithAvailable() {
            if (!this.canFilterByGroups) {
                this.visibleGroupIds = new Set();
                return;
            }

            const availableIds = this.getAvailableGroupIds();

            if (!this.hasUserToggledGroups) {
                this.visibleGroupIds = new Set(availableIds);
                return;
            }

            const next = new Set([...this.visibleGroupIds].filter((id) => availableIds.has(id)));
            if (next.size !== this.visibleGroupIds.size) {
                this.visibleGroupIds = next;
                this.persistGroupFilters();
            }
        },
        async fetchGroups() {
            if (!this.canFilterByGroups) {
                this.groups = [];
                this.visibleGroupIds = new Set();
                return;
            }

            try {
                this.groups = await GroupRepository.getGroupsByClient(this.effectiveClientId);
                this.syncVisibleGroupIdsWithAvailable();
            } catch (e) {
                console.error("Erreur lors du chargement des groupes", e);
                this.groups = [];
            }
        },
        // Reçoit { id, selected } depuis PanelSectionGroups
        onToggleGroup({ id, selected }) {
            this.hasUserToggledGroups = true;
            const next = new Set(this.visibleGroupIds);
            const normalizedId = String(id);
            if (selected) next.add(normalizedId);
            else next.delete(normalizedId);
            this.visibleGroupIds = next;
            this.persistGroupFilters();
        },
        hideAllGroups() {
            this.hasUserToggledGroups = true;
            this.visibleGroupIds = new Set();
            this.persistGroupFilters();
        },
        followMarker(bId) {
            this.$router.push({ name: 'MapFollowBalise', params: { bid: bId } });
        },
        refreshMap() {
            const q = (this.searchInput ?? '').toString().trim();
            if (this.followedBaliseId === null) {
                const promise = this.isAllClientsMode
                    ? BaliseRepository.getAllBalises({ search: q })
                    : BaliseRepository.getBalises(q);

                promise
                    .then((data) => {
                        this.balises = Array.isArray(data) ? data : [];

                        // Ne pas écraser la vue si :
                        // - un geofencing est sélectionné
                        // - ou l'utilisateur a modifié le zoom/centre
                        if (!this.selectedGeofencingId && !this.userViewLocked) {
                            if (data.length === 1) {
                                this.center = [data[0].lastLatitude, data[0].lastLongitude];
                                this.bounds = [];
                            } else if (data.length > 1) {
                                this.bounds = FormatHelper.getCoords(data);
                            }
                        }

                        // Maintenir le contenu du panneau après rafraîchissement
                        if (this.rightPanelMode === 'detail') {
                            this.updateSelectedFromBalises();
                        }
                    })
                    .catch((e) => console.log(e));
            } else {
                BaliseRepository.getBalise(this.followedBaliseId)
                    .then((response) => {
                        this.balises = response ? [response] : [];
                        this.maxBounds = [
                            [response.lastLatitude, response.lastLongitude],
                            [response.lastLatitude, response.lastLongitude],
                        ];
                        this.updateSelectedFromBalises();
                    })
                    .catch((e) => console.log(e));
            }
        },
        cancelAutoUpdate() {
            clearInterval(this.timer);
        },
        setParking(bId) {
            BaliseService.setParking(bId).then(() => this.refreshMap()).catch((e) => console.log(e));
        },
        removeParking(bId) {
            BaliseService.removeParking(bId).then(() => this.refreshMap()).catch((e) => console.log(e));
        },
        async onVehicleReport({ id, from, to }) {
            try {
                const resp = await ReportService.getVehicleReport(id, from, to);
                const { downloadUrl } = resp.data || {};
                if (!downloadUrl) throw new Error('downloadUrl manquant');

                // Construit une URL absolue vers /downloads (même logique que pour les trajets)
                const apiBase = process.env.VUE_APP_API_URL; // ex: https://srv.../api/
                const origin = new URL(apiBase).origin;       // -> https://srv...
                const absolute = new URL(downloadUrl, origin).toString();

                // Ouvre le PDF dans un nouvel onglet via une balise <a>, comme pour les drives
                const a = document.createElement('a');
                a.href = absolute;
                a.target = '_blank';
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                a.remove();
            } catch (e) {
                console.error('Erreur lors du téléchargement du rapport véhicule :', e);
            }
        },

        // --- Geofencings ---
        async fetchGeofencings(term) {
            if (!this.effectiveClientId) {
                this.geofencings = [];
                this.selectedGeofencingId = null;
                this.selectedGeofencingPolygon = [];
                return;
            }
            this.loadingGeofencingList = true;
            try {
                const search = (term || "").trim();
                const { data } = await GeofencingService.getClientGeofencings(this.effectiveClientId, search);
                this.geofencings = data || [];
                // si le geofencing sélectionné n’existe plus, on nettoie
                if (this.selectedGeofencingId) {
                    const exists = this.geofencings.some(g => g.id === this.selectedGeofencingId);
                    if (!exists) {
                        this.selectedGeofencingId = null;
                        this.selectedGeofencingPolygon = [];
                    }
                }
            } catch (e) {
                console.error(e);
            } finally {
                this.loadingGeofencingList = false;
            }
        },

        async handleSelectGeofencing(id) {
            this.selectedGeofencingId = id;
            this.selectedGeofencingPolygon = [];

            // Aucun geofencing sélectionné -> on laisse la carte telle quelle
            if (!id) return;

            this.loadingGeofencingGeom = true;
            try {
                const { data: pts } = await GeofencingService.getGeofencingPoints(id);

                const raw = (pts || [])
                    .slice()
                    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

                // Nettoyage : conversion + filtre des points invalides
                const cleaned = raw
                    .map(p => ({
                        lat: Number(p.latitude),
                        lng: Number(p.longitude),
                    }))
                    .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));

                // Polygone pour Leaflet (array de [lat, lng])
                this.selectedGeofencingPolygon = cleaned.map(p => [p.lat, p.lng]);

                // Auto-zoom sur le geofencing sélectionné
                if (cleaned.length >= 1) {
                    // computePaddedBounds attend [{lat, lng}, ...] + info panneau droit
                    this.bounds = computePaddedBounds(cleaned, this.rightPanelOpen);
                }
            } catch (e) {
                console.error(e);
            } finally {
                this.loadingGeofencingGeom = false;
            }
        },
        // --- Zones liées au véhicule sélectionné ---
        clearVehicleZones() {
            this.vehicleGeofencingId = null;
            this.vehicleGeofencingPolygon = [];
            this.vehicleCorridoringId = null;
            this.vehicleCorridoringPositions = [];
            this.vehicleCorridoringPolyline = null;
            this.vehicleCorridoringStart = null;
            this.vehicleCorridoringEnd = null;
        },

        async loadVehicleZones(balise) {
            if (!balise) {
                this.clearVehicleZones();
                return;
            }

            const newGeoId = balise.geofencingId || null;
            const newCorrId = balise.corridoringId || null;

            if (newGeoId !== this.vehicleGeofencingId) {
                this.vehicleGeofencingId = newGeoId;
                await this.loadVehicleGeofencing(newGeoId);
            }

            if (newCorrId !== this.vehicleCorridoringId) {
                this.vehicleCorridoringId = newCorrId;
                await this.loadVehicleCorridoring(newCorrId);
            }
        },

        async loadVehicleGeofencing(geofencingId) {
            if (!geofencingId) {
                this.vehicleGeofencingPolygon = [];
                return;
            }
            try {
                const { data: pts } = await GeofencingService.getGeofencingPoints(geofencingId);
                const raw = (pts || [])
                    .slice()
                    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

                const cleaned = raw
                    .map(p => [Number(p.latitude), Number(p.longitude)])
                    .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));

                this.vehicleGeofencingPolygon = cleaned;
            } catch (e) {
                console.error('Erreur chargement geofencing véhicule', e);
                this.vehicleGeofencingPolygon = [];
            }
        },

        async loadVehicleCorridoring(corridoringId) {
            if (!corridoringId) {
                this.vehicleCorridoringPositions = [];
                this.vehicleCorridoringPolyline = null;
                this.vehicleCorridoringStart = null;
                this.vehicleCorridoringEnd = null;
                return;
            }
            try {
                const { data } = await CorridoringService.getCorridoringPositions(corridoringId);
                const positions = normalizePositions(data || []);

                this.vehicleCorridoringPositions = positions;
                this.vehicleCorridoringStart = positions[0] || null;
                this.vehicleCorridoringEnd = positions[positions.length - 1] || null;
                this.vehicleCorridoringPolyline = getCorridoringPolyline(positions);
            } catch (e) {
                console.error('Erreur chargement corridoring véhicule', e);
                this.vehicleCorridoringPositions = [];
                this.vehicleCorridoringPolyline = null;
                this.vehicleCorridoringStart = null;
                this.vehicleCorridoringEnd = null;
            }
        },
    },
};
</script>
